import { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Badge,
    Box,
    Button,
    HStack,
    Spinner,
    Text,
    VStack,
} from "@chakra-ui/react";
import { HiPaperClip } from "react-icons/hi2";
import dayjs from "dayjs";
import {
    buildResourceUrl,
    formatMetadataDisplay,
    getTimelineBadgeProps,
    getTimelineEventDescription,
    getTimelineIcon,
    parseMetadata,
} from "./timelineHelpers";

const CandidateTimelineTab = ({ timeline, isLoading, candidateId, onAddEntry, highlightId, onHighlightClear }) => {
    const { t } = useTranslation("models");
    const itemRefs = useRef({});
    const [activeHighlight, setActiveHighlight] = useState(highlightId ?? null);

    useEffect(() => {
        if (!activeHighlight) return;
        const el = itemRefs.current[activeHighlight];
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, [timeline, activeHighlight]);

    useEffect(() => {
        if (!activeHighlight) return;
        const timer = setTimeout(() => {
            setActiveHighlight(null);
            onHighlightClear?.();
        }, 2500);
        return () => clearTimeout(timer);
    }, [activeHighlight]);

    return (
        <Box>
            <HStack justify="space-between" mb={4}>
                <Text color="gray.500" fontSize="sm">
                    {!isLoading && t("candidateDetailPage.eventsCount", { count: timeline.length })}
                </Text>
                <Button variant="outlined" size="sm" onClick={onAddEntry}>
                    {t("candidateDetailPage.addTimelineEntry")}
                </Button>
            </HStack>
            {isLoading ? (
                <VStack py={8} align="center"><Spinner color="purple.400" size="sm" /></VStack>
            ) : timeline.length === 0 ? (
                <VStack py={10} align="center">
                    <Text color="gray.500" fontSize="sm">{t("candidateDetailPage.noTimeline")}</Text>
                </VStack>
            ) : (
                <VStack gap={0} align="stretch">
                    {timeline.map((event, i) => {
                        const badge = getTimelineBadgeProps(event.kind, t);
                        const description = getTimelineEventDescription(event, t);
                        const metaDisplay = formatMetadataDisplay(event, t);
                        const isLast = i === timeline.length - 1;
                        const eventMeta = parseMetadata(event.metadata);
                        const eventKind = (event.kind || '').toLowerCase();
                        const noteDisplay = eventKind === 'statuschange' ? (eventMeta?.note ?? eventMeta?.Note ?? null) : null;
                        const emailBody = eventKind === 'email' ? (eventMeta?.body ?? eventMeta?.Body ?? null) : null;
                        return (
                            <HStack key={event.id} ref={el => { itemRefs.current[event.id] = el; }} align="stretch" gap={4}>
                                <VStack gap={0} align="center" flexShrink={0} w="36px">
                                    <Box
                                        w="36px" h="36px"
                                        borderRadius="full"
                                        bg="gray.50"
                                        border="1px solid"
                                        borderColor="gray.200"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        color="gray.500"
                                        fontSize="sm"
                                        flexShrink={0}
                                    >
                                        {getTimelineIcon(event.kind)}
                                    </Box>
                                    {!isLast && <Box w="1px" bg="gray.200" flex={1} minH="20px" />}
                                </VStack>
                                <Box flex={1} pb={isLast ? 0 : 6}>
                                <Box
                                    bg="white"
                                    border="1px solid"
                                    borderColor={activeHighlight === event.id ? "purple.400" : "gray.200"}
                                    borderRadius={10}
                                    p={4}
                                    transition="border-color 0.5s"
                                    boxShadow={activeHighlight === event.id ? "0 0 0 3px color-mix(in srgb, var(--chakra-colors-purple-400) 30%, transparent)" : undefined}
                                >
                                    <HStack justify="space-between" mb={description || metaDisplay ? 2 : 0} flexWrap="wrap" gap={1}>
                                        <HStack gap={2}>
                                            <Badge
                                                variant="subtle"
                                                colorPalette={badge.colorPalette}
                                                borderRadius={6}
                                                fontSize="xs"
                                                fontWeight={600}
                                            >
                                                {badge.label}
                                            </Badge>
                                            <Text color="gray.500" fontSize="xs">{event.authorFullName ?? "System"}</Text>
                                        </HStack>
                                        <Text color="gray.500" fontSize="xs">
                                            {dayjs(event.occurredAt).format("DD.MM.YYYY HH:mm")}
                                        </Text>
                                    </HStack>
                                    {description && (
                                        <Text color="gray.900" fontSize="sm" mb={metaDisplay ? 2 : 0}>{description}</Text>
                                    )}
                                    {metaDisplay && (
                                        <Box bg="gray.50" borderRadius={8} px={3} py={2}>
                                            <Text fontFamily="mono" fontSize="xs" color="gray.500">{metaDisplay}</Text>
                                        </Box>
                                    )}
                                    {emailBody && (
                                        <Text color="gray.500" fontSize="xs" mt={metaDisplay ? 2 : 0} whiteSpace="pre-wrap">
                                            {emailBody}
                                        </Text>
                                    )}
                                    {noteDisplay && (
                                        <Text color="gray.900" fontSize="sm" mt={metaDisplay ? 2 : 0} fontStyle="italic">Notatka: {noteDisplay}</Text>
                                    )}
                                    {(() => {
                                        const resources = eventMeta?.Resources ?? eventMeta?.resources;
                                        if (!resources?.length) return null;
                                        return (
                                            <HStack flexWrap="wrap" gap={2} mt={description || metaDisplay || noteDisplay ? 2 : 0}>
                                                {resources.map((r, idx) => (
                                                    <HStack
                                                        key={idx}
                                                        as="a"
                                                        href={buildResourceUrl(candidateId, r.Path ?? r.path)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        gap={2} px={3} py={2}
                                                        bg="gray.50" borderRadius={8}
                                                        cursor="pointer" _hover={{ bg: "purple.50" }}
                                                    >
                                                        <HiPaperClip style={{ flexShrink: 0 }} />
                                                        <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">{r.Name ?? r.name}</Text>
                                                    </HStack>
                                                ))}
                                            </HStack>
                                        );
                                    })()}
                                </Box>
                                </Box>
                            </HStack>
                        );
                    })}
                </VStack>
            )}
        </Box>
    );
};

export default CandidateTimelineTab;
